import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { proxyMediaUrl } from "@/lib/media";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AmbientBg, ToolChip } from "@/components/studio/atoms";
import { useAuth } from "@/context/AuthContext";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Loader2, Plus, X, Play, Film,
  ArrowLeft, ArrowRight, Download, Send, Scissors, Wand2,
  Zap, Waves, Undo2, Redo2, ZoomIn, ZoomOut, Copy, Split, Gauge,
  SkipBack, SkipForward, Camera, Save, Volume2, VolumeX, Link2, Sparkles,
  Layers, PinIcon,
} from "lucide-react";

const TRANSITIONS = [
  { id: "cut", label: "Cut", icon: Zap, hint: "Hard cut · zero-frame edit" },
  { id: "crossfade", label: "Dissolve", icon: Waves, hint: "Smooth dissolve · 0.6s" },
  { id: "fade", label: "Fade", icon: Film, hint: "Dip through black · 0.6s" },
];

const ASPECTS = [
  { id: "9:16", label: "Vertical 9:16" },
  { id: "16:9", label: "Cinema 16:9" },
  { id: "1:1", label: "Square 1:1" },
];

const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2];
const ZOOM_LEVELS = [12, 18, 24, 36, 54, 80]; // pixels per second
const DEFAULT_ZOOM_INDEX = 2; // 24 px/s
const FRAME_STEP = 1 / 30;    // assume 30fps for stepping

const DEFAULT_IMAGE_DURATION = 3;
const DEFAULT_VIDEO_DURATION = 5;

const cloneClip = (c) => ({ ...c, clipId: `${c.clipId.split("__")[0]}__${Date.now()}_${Math.random().toString(36).slice(2, 6)}` });

export default function TimelineEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Core state
  const [gallery, setGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [title, setTitle] = useState("Timeline Export");
  const [aspect, setAspect] = useState("9:16");
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const draggingIdxRef = useRef(null);

  // Preview & frame stepping
  const previewVideoRef = useRef(null);
  const [playhead, setPlayhead] = useState(0); // seconds into current selected clip
  // Timeline-strip drag state — used for the "throw a clip back into the media library" flow
  const [libraryDragOver, setLibraryDragOver] = useState(false);
  const [pendingLibDrop, setPendingLibDrop] = useState(false);

  // Undo/redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const skipHistoryRef = useRef(false);

  // Zoom
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM_INDEX);
  const pxPerSec = ZOOM_LEVELS[zoomIdx];

  // Still-frame capture
  const [capturedFrame, setCapturedFrame] = useState(null); // {dataUrl, generation_id?, image_url?}
  // Capture-frame popup — appears immediately after a snapshot with two
  // CTAs (Save to Gallery · Use as Reference). Modal → click-outside or ×
  // dismisses. When the user picks "Use as Reference" we route to /studio
  // and push the frame straight into the attachment rail.
  const [showCaptureDialog, setShowCaptureDialog] = useState(false);
  const [captureBusy, setCaptureBusy] = useState(null); // "save" | "reference" | null
  // Frame-series state — populated by `captureFrameSeries()` when the user
  // hits the multi-shot toolbar button. `frames` is up to 3 entries, each
  // shaped identically to a single `capturedFrame` payload. `active` is the
  // currently-selected index the primary CTAs act on. Bulk buttons operate
  // on the whole list (Save All / Attach All).
  const [capturedSeries, setCapturedSeries] = useState(null); // {frames:[], active:number}
  const [seriesBusy, setSeriesBusy] = useState(null); // "save-all" | "attach-all" | idx-N | null

  // ---------- History helpers ----------
  const pushHistory = useCallback((snapshot) => {
    setUndoStack((prev) => [...prev.slice(-49), snapshot]);
    setRedoStack([]);
  }, []);

  // Snapshot current timeline BEFORE mutations
  const mutate = useCallback((mutator) => {
    setTimeline((prev) => {
      pushHistory(prev);
      const next = mutator(prev);
      return next;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [...r, timeline]);
      skipHistoryRef.current = true;
      setTimeline(last);
      return prev.slice(0, -1);
    });
  }, [timeline]);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setUndoStack((u) => [...u, timeline]);
      skipHistoryRef.current = true;
      setTimeline(next);
      return prev.slice(0, -1);
    });
  }, [timeline]);

  // Keyboard shortcuts (Cmd/Ctrl+Z, Shift+Z or Y = redo)
  useEffect(() => {
    const handler = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // ---------- Gallery load ----------
  useEffect(() => {
    if (!user) return;
    setLoadingGallery(true);
    api.get("/generations?scope=mine")
      .then((r) => setGallery((r.data || []).filter((g) => g.video_url)))
      .catch(() => toast.error("Failed to load gallery"))
      .finally(() => setLoadingGallery(false));
  }, [user]);

  // ---------- Timeline mutations ----------
  const addToTimeline = (g) => {
    const isVideo = !!g.video_url;
    const clip = {
      clipId: `${g.generation_id}__${Date.now()}`,
      source_url: g.video_url || g.image_url,
      kind: isVideo ? "video" : "image",
      thumb: g.image_url || g.video_url,
      name: (g.prompt || "").slice(0, 40) || (isVideo ? "Video clip" : "Image"),
      duration: isVideo ? DEFAULT_VIDEO_DURATION : DEFAULT_IMAGE_DURATION,
      start: 0,
      transition: "cut",
      speed: 1,
      volume: 1,
      source_aspect: g.aspect_ratio || null,
    };
    mutate((prev) => [...prev, clip]);
    if (timeline.length === 0) {
      if (g.aspect_ratio && ["9:16", "16:9", "1:1"].includes(g.aspect_ratio)) {
        setAspect(g.aspect_ratio);
      } else if (typeof window !== "undefined" && (g.image_url || g.video_url)) {
        const probe = new Image();
        probe.onload = () => {
          const r = probe.naturalWidth / probe.naturalHeight;
          const guess = r > 1.4 ? "16:9" : r < 0.7 ? "9:16" : "1:1";
          setAspect(guess);
          setTimeline((tl) => tl.length && tl[0].clipId === clip.clipId
            ? [{ ...tl[0], source_aspect: guess }, ...tl.slice(1)]
            : tl);
        };
        probe.src = g.image_url || g.video_url;
      }
    }
    // Select the newly-added clip. Use functional length lookup to avoid stale closure.
    setTimeline((prev) => { setSelectedIdx(prev.length - 1); return prev; });
    toast.success(`Added ${isVideo ? "clip" : "still"} to timeline.`);
  };

  const removeClip = (idx) => {
    mutate((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIdx(null);
  };

  const duplicateClip = (idx) => {
    if (idx == null || !timeline[idx]) return;
    const copy = cloneClip(timeline[idx]);
    mutate((prev) => [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)]);
    setSelectedIdx(idx + 1);
    toast.success("Clip duplicated.");
  };

  const splitClip = (idx) => {
    const c = timeline[idx];
    if (!c) return;
    if (c.kind !== "video") { toast.error("Only video clips can be split."); return; }
    const ph = Math.max(0.2, Math.min(playhead, c.duration - 0.2));
    if (ph >= c.duration - 0.2) { toast.error("Move the playhead into the clip to split."); return; }
    const first = { ...c, clipId: `${c.clipId}__A`, duration: ph };
    const second = { ...c, clipId: `${c.clipId}__B`, start: (Number(c.start) || 0) + ph, duration: c.duration - ph, transition: "cut" };
    mutate((prev) => [...prev.slice(0, idx), first, second, ...prev.slice(idx + 1)]);
    setSelectedIdx(idx + 1);
    setPlayhead(0);
    toast.success(`Clip split at ${ph.toFixed(2)}s.`);
  };

  const moveClip = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= timeline.length) return;
    mutate((prev) => {
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
    setSelectedIdx(j);
  };

  const reorderByDrag = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    mutate((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setSelectedIdx(toIdx);
  };

  const updateSelected = (patch) => {
    if (selectedIdx == null) return;
    mutate((prev) => prev.map((c, i) => (i === selectedIdx ? { ...c, ...patch } : c)));
  };

  // ---------- Playhead / frame stepping ----------
  const stepFrame = (dir) => {
    const v = previewVideoRef.current;
    if (!v) return;
    v.pause();
    const next = Math.max(0, Math.min((v.duration || 30), (v.currentTime || 0) + dir * FRAME_STEP));
    v.currentTime = next;
    setPlayhead(next);
  };

  const onPreviewTimeUpdate = () => {
    const v = previewVideoRef.current;
    if (!v) return;
    setPlayhead(v.currentTime);
  };

  // Apply per-clip speed & volume to the preview element live
  useEffect(() => {
    const v = previewVideoRef.current;
    if (!v || selectedIdx == null || !timeline[selectedIdx]) return;
    const c = timeline[selectedIdx];
    try {
      v.playbackRate = Number(c.speed) || 1;
      v.volume = Math.max(0, Math.min(1, Number(c.volume ?? 1)));
    } catch (err) {
      // Video not yet loaded — Safari/older browsers can throw before metadata; safe to swallow
      console.debug("clip a/v attach deferred", err);
    }
  }, [selectedIdx, timeline]);

  // ---------- Still-frame capture ----------
  const captureFrame = () => {
    const v = previewVideoRef.current;
    if (!v) { toast.error("Select a video clip first."); return; }
    if (!v.videoWidth || !v.videoHeight) { toast.error("Video not ready yet."); return; }
    v.pause();
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    // Guard against tainted-canvas SecurityError — a cross-origin CDN video
    // without proper CORS headers makes toDataURL throw. Surface a friendly
    // message instead of crashing the whole page.
    let dataUrl;
    try {
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      dataUrl = canvas.toDataURL("image/png");
    } catch (err) {
      const msg = /SecurityError|tainted/i.test(String(err))
        ? "Can't capture — this video is served without CORS headers."
        : "Frame capture failed. Try a different clip.";
      toast.error(msg);
      return;
    }
    setCapturedFrame({ dataUrl, capturedAt: v.currentTime, width: v.videoWidth, height: v.videoHeight });
    setCapturedSeries(null); // single-frame branch owns the dialog
    setShowCaptureDialog(true);
    toast.success(`Frame captured · ${v.videoWidth}×${v.videoHeight}`);
  };

  // ── Frame Series Capture — 3 frames spaced 1s apart, starting at the
  // current playhead. Seeks the preview video sequentially (fires 'seeked'
  // between each grab), so we get 3 clean stills without an extra decode
  // pipeline. Runs entirely client-side — no upload until the user picks
  // a per-frame or bulk action from the popup.
  const captureFrameSeries = async () => {
    const v = previewVideoRef.current;
    if (!v) { toast.error("Select a video clip first."); return; }
    if (!v.videoWidth || !v.videoHeight) { toast.error("Video not ready yet."); return; }
    v.pause();
    const start = v.currentTime;
    const dur = Number.isFinite(v.duration) ? v.duration : (start + 5);
    // Space 1s apart from `start`. Cap each timestamp to the video's
    // duration so we never seek past the end — otherwise the last frame
    // silently reuses the previous seeked position on Chrome.
    const timestamps = [0, 1, 2].map((k) => Math.min(start + k, Math.max(0, dur - 0.01)));

    const seekTo = (t) => new Promise((resolve) => {
      const onSeeked = () => { v.removeEventListener("seeked", onSeeked); resolve(); };
      v.addEventListener("seeked", onSeeked);
      v.currentTime = t;
    });

    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");

    const frames = [];
    try {
      for (const t of timestamps) {
        await seekTo(t);
        // A tiny extra tick lets Safari flush the decoded frame; without
        // it the first grab can still show the pre-seek pixels.
        await new Promise((r) => setTimeout(r, 40));
        try {
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/png");
          frames.push({ dataUrl, capturedAt: t, width: canvas.width, height: canvas.height });
        } catch (err) {
          const msg = /SecurityError|tainted/i.test(String(err))
            ? "Can't capture — this video is served without CORS headers."
            : "Frame series capture failed. Try a different clip.";
          toast.error(msg);
          return;
        }
      }
    } finally {
      // Restore the playhead to where the user started so the timeline UI
      // doesn't jump on them mid-review.
      v.currentTime = start;
    }

    if (frames.length !== 3) {
      toast.error("Couldn't capture 3 frames — clip may be too short.");
      return;
    }
    setCapturedSeries({ frames, active: 0 });
    setCapturedFrame(null); // dialog uses series branch when this is set
    setShowCaptureDialog(true);
    toast.success(`3-frame series captured · ${canvas.width}×${canvas.height}`);
  };

  // Upload a single series frame (idempotent — reuses existing image_url
  // if already uploaded) and update the frame in-place.
  const uploadSeriesFrame = async (idx) => {
    if (!capturedSeries?.frames?.[idx]) return null;
    const frame = capturedSeries.frames[idx];
    if (frame.image_url && frame.generation_id) {
      return { generation_id: frame.generation_id, image_url: frame.image_url };
    }
    const blob = await (await fetch(frame.dataUrl)).blob();
    const form = new FormData();
    form.append("file", blob, `series_${Date.now()}_${idx}.png`);
    form.append("note", `Still frame ${idx + 1}/3 · Editor series capture @ ${frame.capturedAt.toFixed(2)}s`);
    const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
    setCapturedSeries((prev) => {
      if (!prev) return prev;
      const next = { ...prev, frames: prev.frames.slice() };
      next.frames[idx] = { ...next.frames[idx], generation_id: data.generation_id, image_url: data.image_url };
      return next;
    });
    return { generation_id: data.generation_id, image_url: data.image_url };
  };

  const saveSeriesFrameToGallery = async (idx) => {
    setSeriesBusy(`save-${idx}`);
    try {
      await uploadSeriesFrame(idx);
      toast.success(`Frame ${idx + 1} saved to gallery.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSeriesBusy(null);
    }
  };

  const saveAllSeriesToGallery = async () => {
    if (!capturedSeries?.frames?.length) return;
    setSeriesBusy("save-all");
    let ok = 0;
    for (let i = 0; i < capturedSeries.frames.length; i++) {
      try { await uploadSeriesFrame(i); ok++; } catch (_) {}
    }
    setSeriesBusy(null);
    if (ok === capturedSeries.frames.length) toast.success(`All ${ok} frames saved to gallery.`);
    else toast.warning(`${ok}/${capturedSeries.frames.length} frames saved.`);
  };

  // Attach a single series frame as a reference — same flow as the single-
  // frame "Use as Reference": upload + navigate to /studio with attachRef.
  const attachSeriesFrame = async (idx) => {
    setSeriesBusy(`attach-${idx}`);
    try {
      const uploaded = await uploadSeriesFrame(idx);
      if (!uploaded) throw new Error("upload returned no data");
      setShowCaptureDialog(false);
      toast.success(`Frame ${idx + 1} attached to Studio reference rail.`);
      navigate("/studio", {
        state: {
          attachRef: {
            generation_id: uploaded.generation_id,
            image_url: uploaded.image_url,
            source: "editor-series-frame",
            capturedAt: capturedSeries.frames[idx].capturedAt,
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach as reference");
    } finally {
      setSeriesBusy(null);
    }
  };

  // Attach all 3 frames at once — upload them concurrently then hand the
  // list to Studio via `attachRefBatch`. Studio pushes each into `omniRefs`
  // (respecting the 5-cap and dedupe rules) and fires its own success
  // toast with the accurate landed-count — so we DON'T emit an editor-side
  // success here (avoid double-toast). Only surface a partial-failure
  // warning if some uploads failed before navigation.
  const attachAllSeriesAsReferences = async () => {
    if (!capturedSeries?.frames?.length) return;
    setSeriesBusy("attach-all");
    try {
      const uploaded = await Promise.all(
        capturedSeries.frames.map((_, i) => uploadSeriesFrame(i).catch(() => null))
      );
      const successful = uploaded.filter(Boolean);
      if (!successful.length) throw new Error("All uploads failed");
      const failedCount = capturedSeries.frames.length - successful.length;
      if (failedCount > 0) {
        toast.warning(`${successful.length}/${capturedSeries.frames.length} frames uploaded — ${failedCount} failed.`);
      }
      setShowCaptureDialog(false);
      navigate("/studio", {
        state: {
          attachRefBatch: successful.map((u, i) => ({
            generation_id: u.generation_id,
            image_url: u.image_url,
            source: "editor-series-batch",
            capturedAt: capturedSeries.frames[i]?.capturedAt,
          })),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach series");
    } finally {
      setSeriesBusy(null);
    }
  };

  const downloadFrame = () => {
    if (!capturedFrame?.dataUrl) return;
    const a = document.createElement("a");
    a.href = capturedFrame.dataUrl;
    a.download = `geneo-frame-${Date.now()}.png`;
    a.click();
  };

  // Shared upload — used by both "Save to Gallery" and "Use as Reference".
  // Returns the fresh {generation_id, image_url} on success, else null.
  const uploadCapturedFrame = async () => {
    if (!capturedFrame?.dataUrl) return null;
    if (capturedFrame.image_url && capturedFrame.generation_id) {
      return { generation_id: capturedFrame.generation_id, image_url: capturedFrame.image_url };
    }
    const blob = await (await fetch(capturedFrame.dataUrl)).blob();
    const form = new FormData();
    form.append("file", blob, `frame_${Date.now()}.png`);
    form.append("note", "Still frame from Timeline Editor");
    const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
    setCapturedFrame((f) => f ? { ...f, generation_id: data.generation_id, image_url: data.image_url } : f);
    return { generation_id: data.generation_id, image_url: data.image_url };
  };

  const saveFrameToGallery = async () => {
    if (!capturedFrame?.dataUrl) return;
    setCaptureBusy("save");
    try {
      await uploadCapturedFrame();
      // Note: gallery inside the Editor Media Library is video-only now, so
      // no refresh needed here. The image lands in /gallery and the Studio feed.
      toast.success("Frame saved to your gallery.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setCaptureBusy(null);
    }
  };

  // "Use as Reference" — uploads the frame if not already, then routes to
  // /studio with `attachRef` in location.state. Studio's useEffect picks it
  // up and pushes it straight into the omni-reference rail.
  const useCapturedAsReference = async () => {
    if (!capturedFrame?.dataUrl) return;
    setCaptureBusy("reference");
    try {
      const uploaded = await uploadCapturedFrame();
      if (!uploaded) throw new Error("upload returned no data");
      setShowCaptureDialog(false);
      toast.success("Sent to Studio · attached to reference rail.");
      navigate("/studio", {
        state: {
          attachRef: {
            generation_id: uploaded.generation_id,
            image_url: uploaded.image_url,
            source: "editor-frame-capture",
            capturedAt: capturedFrame.capturedAt,
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach as reference");
    } finally {
      setCaptureBusy(null);
    }
  };

  const sendFrameToPipeline = () => {
    if (!capturedFrame?.image_url || !capturedFrame?.generation_id) {
      toast.error("Save the frame first.");
      return;
    }
    navigate("/studio", {
      state: {
        pipelineRoute: {
          generation_id: capturedFrame.generation_id,
          image_url: capturedFrame.image_url,
          prompt: "Still frame from timeline",
          type: "image",
        },
      },
    });
  };

  // ---------- Export ----------
  const publishExport = async () => {
    if (!exportResult?.generation_id) return;
    setPublishing(true);
    try {
      await api.patch(`/generations/${exportResult.generation_id}/visibility`, { is_public: true });
      setExportResult((r) => r ? { ...r, is_public: true } : r);
      toast.success("Reel sent to the community gallery.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const exportTimeline = async () => {
    if (timeline.length === 0) { toast.error("Add at least one clip."); return; }
    setExporting(true);
    setExportResult(null);
    try {
      const payload = {
        title,
        aspect,
        clips: timeline.map((c) => ({
          source_url: c.source_url,
          kind: c.kind,
          start: Number(c.start) || 0,
          duration: Number(c.duration) || 3,
          transition: c.transition || "cut",
          speed: Number(c.speed) || 1,
          volume: Number(c.volume ?? 1),
        })),
      };
      const { data } = await api.post("/editor/export", payload);
      setExportResult(data);
      toast.success("Timeline exported.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalDuration = timeline.reduce((s, c) => s + (Number(c.duration) || 0), 0);
  const selected = selectedIdx != null ? timeline[selectedIdx] : null;
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  // Absolute playhead position across the entire timeline (in seconds).
  // Sum durations of all clips BEFORE the selected clip, then add per-clip playhead offset.
  const clipStartOffsets = useMemo(() => {
    const arr = [];
    let acc = 0;
    for (const c of timeline) { arr.push(acc); acc += Number(c.duration) || 0; }
    return arr;
  }, [timeline]);
  const absolutePlayhead = selectedIdx != null && clipStartOffsets[selectedIdx] != null
    ? clipStartOffsets[selectedIdx] + Math.min(playhead, Number(selected?.duration) || 0)
    : 0;
  const totalFrames = Math.round(totalDuration * 30);
  const currentFrame = Math.round(absolutePlayhead * 30);

  // Seek from the absolute scrubber — find which clip contains `sec` and seek into it.
  const seekAbsolute = (sec) => {
    if (timeline.length === 0) return;
    const clamped = Math.max(0, Math.min(sec, totalDuration));
    let acc = 0;
    for (let i = 0; i < timeline.length; i++) {
      const d = Number(timeline[i].duration) || 0;
      if (clamped <= acc + d || i === timeline.length - 1) {
        setSelectedIdx(i);
        const intra = Math.max(0, Math.min(clamped - acc, d));
        setPlayhead(intra);
        const v = previewVideoRef.current;
        if (v && timeline[i].kind === "video") {
          try { v.currentTime = intra; } catch (err) { console.debug("seek before metadata", err); }
        }
        return;
      }
      acc += d;
    }
  };

  return (
    <div className="min-h-screen bg-[#07070C] glyph-bg relative">
      <AmbientBg variant="studio" />
      <Navbar />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8">
        {/* Hero */}
        <div className="relative mb-6 rounded-3xl overflow-hidden border border-[#00E5FF]/25 bg-gradient-to-br from-[#0d1f24] via-[#0A0A0A] to-[#1a0d24] p-6 md:p-7 action-stripes">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#00E5FF]/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#00E5FF] mb-3 px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30">
                <Film className="w-3 h-3" /> TIMELINE · v2 · PRO
              </div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                Cut, arrange, and <span className="bg-gradient-to-r from-[#F2C94C] via-[#00E5FF] to-[#EB5757] bg-clip-text text-transparent">ship</span>.
              </h1>
              <p className="font-body text-[#D9D2BE] mt-2 max-w-2xl text-sm md:text-base">
                Split, speed, trim, and capture frames. Full undo/redo with keyboard shortcuts (⌘Z / ⌘⇧Z). Export as one MP4 — 5 credits.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="editor-title-input"
                className="w-56 bg-[#0A0A0A] border-[#F2C94C]/20 text-white font-body"
                placeholder="Timeline title"
              />
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger data-testid="editor-aspect-select" className="w-40 bg-[#0A0A0A] border-[#F2C94C]/20 font-body">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#120E15] border-[#F2C94C]/20">
                  {ASPECTS.map((a) => (<SelectItem key={a.id} value={a.id} className="font-body">{a.label}</SelectItem>))}
                </SelectContent>
              </Select>
              {timeline.length > 0 && timeline[0]?.source_aspect && timeline[0].source_aspect === aspect && (
                <div className="font-mono-hy text-[9px] text-[#00E5FF] flex items-center gap-1" data-testid="editor-aspect-auto">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] pulse-ring" />
                  AUTO
                </div>
              )}
              <ToolChip
                icon={Download}
                label={exporting ? "Rendering…" : "Export · 5 cr"}
                onClick={exportTimeline}
                disabled={exporting || timeline.length === 0}
                loading={exporting}
                accent="gold"
                shape="pill"
                variant="solid"
                size="md"
                testId="editor-export-btn"
              />
            </div>
          </div>
        </div>

        {/* Pro Toolbar */}
        <div className="mb-3 flex items-center gap-1.5 flex-wrap p-3 rounded-3xl bg-gradient-to-br from-[#0e0b13]/85 via-[#0a0812]/85 to-[#0a0a0a]/85 border border-white/[0.06] backdrop-blur-xl shadow-[0_0_36px_-14px_rgba(242,201,76,0.28),0_0_36px_-14px_rgba(0,229,255,0.22)]" data-testid="editor-toolbar">
          <ToolbarBtn onClick={undo} disabled={!canUndo} testid="tool-undo" icon={Undo2} label="Undo" accent="cyan" />
          <ToolbarBtn onClick={redo} disabled={!canRedo} testid="tool-redo" icon={Redo2} label="Redo" accent="cyan" />
          <div className="w-px h-6 bg-[#F2C94C]/15 mx-1" />
          <ToolbarBtn onClick={() => selectedIdx != null && splitClip(selectedIdx)} disabled={!selected || selected.kind !== "video"} testid="tool-split" icon={Split} label="Split" accent="gold" />
          <ToolbarBtn onClick={() => selectedIdx != null && duplicateClip(selectedIdx)} disabled={!selected} testid="tool-duplicate" icon={Copy} label="Duplicate" accent="gold" />
          <ToolbarBtn onClick={() => selectedIdx != null && removeClip(selectedIdx)} disabled={!selected} testid="tool-delete" icon={X} label="Delete" accent="red" />
          <div className="w-px h-6 bg-[#F2C94C]/15 mx-1" />
          <ToolbarBtn onClick={() => stepFrame(-1)} disabled={!selected || selected.kind !== "video"} testid="tool-frame-back" icon={SkipBack} label="◀ Frame" accent="cyan" />
          <ToolbarBtn onClick={() => stepFrame(1)} disabled={!selected || selected.kind !== "video"} testid="tool-frame-fwd" icon={SkipForward} label="Frame ▶" accent="cyan" />
          <ToolbarBtn onClick={captureFrame} disabled={!selected || selected.kind !== "video"} testid="tool-capture-frame" icon={Camera} label="Capture Frame" accent="gold" />
          <ToolbarBtn onClick={captureFrameSeries} disabled={!selected || selected.kind !== "video"} testid="tool-capture-series" icon={Layers} label="Capture Series · 3× 1s" accent="cyan" />
          <div className="w-px h-6 bg-[#F2C94C]/15 mx-1" />
          <ToolbarBtn onClick={() => setZoomIdx((i) => Math.max(0, i - 1))} disabled={zoomIdx === 0} testid="tool-zoom-out" icon={ZoomOut} label="" accent="cyan" />
          <div className="font-mono-hy text-[10px] text-[#00E5FF] tabular-nums px-2" data-testid="tool-zoom-value">{pxPerSec}px/s</div>
          <ToolbarBtn onClick={() => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1} testid="tool-zoom-in" icon={ZoomIn} label="" accent="cyan" />
          <div className="flex-1" />
          {selected && (
            <div className="font-mono-hy text-[10px] text-white">
              PLAYHEAD · <span className="text-[#F2C94C] tabular-nums">{playhead.toFixed(2)}s</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left · Media library */}
          <aside className="col-span-12 lg:col-span-3">
            <div
              className={`p-4 rounded-2xl bg-[#120E15] border h-[560px] overflow-hidden flex flex-col transition-all ${
                libraryDragOver ? "border-[#E27D60] shadow-[0_0_24px_-6px_rgba(226,125,96,0.6)]" : "border-[#F2C94C]/15"
              }`}
              onDragOver={(e) => {
                if (e.dataTransfer?.types?.includes("application/geneo-timeline-idx")) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setLibraryDragOver(true);
                }
              }}
              onDragLeave={() => setLibraryDragOver(false)}
              onDrop={(e) => {
                setLibraryDragOver(false);
                const raw = e.dataTransfer?.getData("application/geneo-timeline-idx");
                if (!raw) return;
                e.preventDefault();
                const idx = parseInt(raw, 10);
                if (!Number.isNaN(idx)) {
                  removeClip(idx);
                  toast.success("Clip returned to the library.");
                }
              }}
              data-testid="media-library-dropzone"
            >
              <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3 flex items-center gap-2">
                <Film className="w-3 h-3" /> // Video library
                {libraryDragOver && <span className="text-[#E27D60]">· drop to remove</span>}
              </div>
              {loadingGallery ? (
                <div className="flex-1 flex justify-center items-center"><Loader2 className="w-4 h-4 animate-spin text-[#F2C94C]" /></div>
              ) : gallery.length === 0 ? (
                <div className="flex-1 flex flex-col justify-center items-center text-center px-4">
                  <Film className="w-10 h-10 text-[#F2C94C]/30 mb-2" />
                  <div className="font-mono-hy text-[10px] text-white mb-3">No video clips yet.</div>
                  <a href="/studio" className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] flex items-center gap-2">
                    <Wand2 className="w-3.5 h-3.5" /> Create in Studio
                  </a>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 overflow-y-auto pr-1" data-testid="media-library">
                  {gallery.map((g) => (
                    <button
                      key={g.generation_id}
                      onClick={() => addToTimeline(g)}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.effectAllowed = "copy";
                          e.dataTransfer.setData("application/geneo-media-id", g.generation_id);
                        } catch (err) { console.debug("dnd media-id set failed", err); }
                      }}
                      data-testid={`media-add-${g.generation_id}`}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-[#F2C94C]/15 hover:border-[#F2C94C] transition-all"
                    >
                      {/* Poster: prefer the generation's own thumbnail image_url
                          (populated on most video gens); fall back to a first-frame
                          scrape of the video via the browser. */}
                      <img src={proxyMediaUrl(g.image_url || g.video_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 to-transparent pointer-events-none" />
                      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between pointer-events-none">
                        <span className="font-mono-hy text-[8px] text-white flex items-center gap-1">
                          <Film className="w-2.5 h-2.5" /> VIDEO
                        </span>
                        <span className="w-5 h-5 rounded-full bg-[#F2C94C] text-[#0A0A0A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                          <Plus className="w-3 h-3" />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Center · Preview + Clip Inspector */}
          <main className="col-span-12 lg:col-span-6 space-y-4">
            <div className="rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 h-[400px] overflow-hidden flex items-center justify-center relative" data-testid="editor-preview">
              {exportResult?.video_url ? (
                <video src={proxyMediaUrl(exportResult.video_url)} crossOrigin="anonymous" controls autoPlay loop className="max-h-full max-w-full" data-testid="export-preview" />
              ) : selected ? (
                selected.kind === "video" ? (
                  <video
                    ref={previewVideoRef}
                    key={selected.clipId}
                    // Route external (fal.ai) URLs through our /api/proxy/media
                    // proxy so the video is same-origin from the browser's
                    // perspective. Combined with crossOrigin="anonymous" this
                    // makes canvas.toDataURL work without SecurityError.
                    src={proxyMediaUrl(selected.source_url)}
                    crossOrigin="anonymous"
                    controls
                    className="max-h-full max-w-full"
                    playsInline
                    onTimeUpdate={onPreviewTimeUpdate}
                    onLoadedMetadata={() => setPlayhead(0)}
                    data-testid="preview-video"
                  />
                ) : (
                  <img src={proxyMediaUrl(selected.thumb)} alt={selected.name} crossOrigin="anonymous" className="max-h-full max-w-full" />
                )
              ) : (
                <div className="text-center p-8">
                  <Film className="w-14 h-14 text-[#00E5FF]/30 mx-auto mb-3" />
                  <div className="font-mono-hy text-[10px] text-white mb-1">// PREVIEW</div>
                  <p className="font-display text-lg text-white">Add clips to preview and arrange your timeline.</p>
                </div>
              )}
            </div>

            {/* Captured frame — inline confirmation strip.
                Shown briefly after the popup is dismissed so the user can
                re-open the actions from a smaller footprint. The primary
                CTA popup lives in <Dialog> below this JSX tree. */}
            {capturedFrame && !showCaptureDialog && (
              <div className="p-3 rounded-2xl bg-[#120E15] border border-[#F2C94C]/40 flex items-center gap-3 flex-wrap" data-testid="captured-frame-panel">
                <img src={capturedFrame.dataUrl} alt="captured" className="w-14 h-14 rounded-lg object-cover border border-[#F2C94C]/30" data-testid="captured-frame-thumb" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono-hy text-[10px] text-[#F2C94C]">STILL FRAME · {(capturedFrame.width || 0)}×{(capturedFrame.height || 0)}</div>
                  <div className="font-body text-xs text-white">Captured at {Number(capturedFrame.capturedAt || 0).toFixed(2)}s{capturedFrame.image_url ? " · Saved to gallery ✓" : ""}</div>
                </div>
                <button onClick={() => setShowCaptureDialog(true)} data-testid="frame-open-dialog-btn" className="font-mono-hy text-[10px] px-3 py-2 rounded-full bg-gradient-to-r from-[#F2C94C] to-[#00E5FF] text-[#0A0A0A] hover:brightness-110 flex items-center gap-1.5">
                  <Camera className="w-3 h-3" /> Options
                </button>
                <button onClick={downloadFrame} data-testid="frame-download-btn" className="font-mono-hy text-[10px] px-3 py-2 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center gap-1.5">
                  <Download className="w-3 h-3" /> PNG
                </button>
                <button onClick={() => setCapturedFrame(null)} data-testid="frame-dismiss-btn" className="w-7 h-7 rounded-full text-white hover:text-[#E27D60] flex items-center justify-center">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Clip Inspector */}
            {selected && (
              <div className="p-4 rounded-2xl bg-[#120E15] border border-[#00E5FF]/25 space-y-3" data-testid="clip-inspector">
                <div className="flex items-center gap-3 flex-wrap">
                  <img src={selected.thumb} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#00E5FF]/30" />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm text-white truncate">{selected.name}</div>
                    <div className="font-mono-hy text-[9px] text-white uppercase">{selected.kind} · {Number(selected.duration).toFixed(1)}s{selected.speed !== 1 ? ` @ ${selected.speed}x` : ""}</div>
                  </div>
                  <button onClick={() => moveClip(selectedIdx, -1)} disabled={selectedIdx === 0} data-testid="clip-move-left" className="w-8 h-8 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] disabled:opacity-30 flex items-center justify-center"><ArrowLeft className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveClip(selectedIdx, 1)} disabled={selectedIdx === timeline.length - 1} data-testid="clip-move-right" className="w-8 h-8 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] disabled:opacity-30 flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></button>
                  <button onClick={() => duplicateClip(selectedIdx)} data-testid="clip-duplicate" className="w-8 h-8 rounded-full border border-[#F2C94C]/20 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center justify-center" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeClip(selectedIdx)} data-testid="clip-remove" className="w-8 h-8 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>

                {/* Duration */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-mono-hy text-[9px] text-white flex items-center gap-1"><Scissors className="w-3 h-3" /> Duration (Out - In)</label>
                    <span className="font-mono-hy text-[10px] text-[#F2C94C]">{Number(selected.duration).toFixed(1)}s</span>
                  </div>
                  <Slider
                    value={[Number(selected.duration) || 3]}
                    onValueChange={(v) => updateSelected({ duration: v[0] })}
                    min={0.5}
                    max={selected.kind === "video" ? 30 : 8}
                    step={0.5}
                    data-testid="clip-duration-slider"
                  />
                </div>

                {/* Trim In */}
                {selected.kind === "video" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="font-mono-hy text-[9px] text-white">Trim In-point</label>
                      <span className="font-mono-hy text-[10px] text-[#00E5FF]">{Number(selected.start).toFixed(1)}s</span>
                    </div>
                    <Slider
                      value={[Number(selected.start) || 0]}
                      onValueChange={(v) => updateSelected({ start: v[0] })}
                      min={0}
                      max={30}
                      step={0.1}
                      data-testid="clip-start-slider"
                    />
                  </div>
                )}

                {/* Speed */}
                {selected.kind === "video" && (
                  <div className="pt-2 border-t border-[#F2C94C]/10">
                    <label className="font-mono-hy text-[9px] text-white mb-1.5 flex items-center gap-1">
                      <Gauge className="w-3 h-3" /> Playback Speed
                    </label>
                    <div className="grid grid-cols-5 gap-1.5" data-testid="clip-speed-picker">
                      {SPEED_OPTIONS.map((sp) => {
                        const active = Number(selected.speed || 1) === sp;
                        return (
                          <button
                            key={sp}
                            onClick={() => updateSelected({ speed: sp })}
                            data-testid={`clip-speed-${sp}`}
                            className={`px-2 py-1.5 rounded-lg font-mono-hy text-[10px] transition-all border ${
                              active
                                ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF]"
                                : "border-[#F2C94C]/15 text-white hover:border-[#F2C94C]/40 hover:text-[#F2C94C]"
                            }`}
                          >
                            {sp}x
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Volume per clip */}
                <div className="pt-2 border-t border-[#F2C94C]/10">
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-mono-hy text-[9px] text-white flex items-center gap-1">
                      {Number(selected.volume ?? 1) > 0 ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />} Clip Volume
                    </label>
                    <span className="font-mono-hy text-[10px] text-[#00E5FF] tabular-nums">{Math.round(Number(selected.volume ?? 1) * 100)}%</span>
                  </div>
                  <Slider
                    value={[Number(selected.volume ?? 1) * 100]}
                    onValueChange={(v) => updateSelected({ volume: v[0] / 100 })}
                    min={0}
                    max={100}
                    step={1}
                    data-testid="clip-volume-slider"
                  />
                </div>

                {/* Incoming transition */}
                {selectedIdx > 0 && (
                  <div className="pt-3 border-t border-[#F2C94C]/10">
                    <label className="font-mono-hy text-[9px] text-white mb-2 flex items-center gap-1">
                      <Waves className="w-3 h-3" /> Incoming Transition
                    </label>
                    <div className="grid grid-cols-3 gap-2" data-testid="clip-transition-picker">
                      {TRANSITIONS.map((t) => {
                        const Icon = t.icon;
                        const active = (selected.transition || "cut") === t.id;
                        return (
                          <button
                            key={t.id}
                            onClick={() => updateSelected({ transition: t.id })}
                            data-testid={`clip-transition-${t.id}`}
                            title={t.hint}
                            className={`p-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${
                              active
                                ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                                : "border-[#F2C94C]/15 text-white hover:border-[#F2C94C]/40 hover:text-[#F2C94C]"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            <span className="font-mono-hy text-[9px]">{t.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {exportResult && (
              <div className="p-3 rounded-2xl bg-[#120E15] border border-[#F2C94C]/25 flex items-center gap-3 flex-wrap" data-testid="export-result-strip">
                <div className="w-10 h-10 rounded-lg bg-[#F2C94C] text-[#0A0A0A] flex items-center justify-center">
                  <Film className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-hy text-[10px] text-[#F2C94C]">TIMELINE EXPORTED</div>
                  <div className="font-body text-xs text-white truncate">{exportResult.prompt || title}</div>
                </div>
                <button
                  onClick={publishExport}
                  disabled={publishing || exportResult.is_public}
                  data-testid="editor-send-to-gallery-btn"
                  className={`font-mono-hy text-[10px] px-4 py-2 rounded-full transition-all flex items-center gap-2 disabled:cursor-not-allowed ${
                    exportResult.is_public
                      ? "bg-[#27AE60]/20 border border-[#27AE60] text-[#27AE60]"
                      : "border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                  }`}
                >
                  {publishing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publishing…</> : exportResult.is_public ? <><Send className="w-3.5 h-3.5" /> In Gallery</> : <><Send className="w-3.5 h-3.5" /> Send to Gallery</>}
                </button>
                <a
                  href={exportResult.video_url}
                  download={`${(exportResult.prompt || "geneo-timeline").replace(/[^a-z0-9]+/gi, "-")}.mp4`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="export-download-link"
                  className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" /> Download MP4
                </a>
              </div>
            )}
          </main>

          {/* Right · Session stats */}
          <aside className="col-span-12 lg:col-span-3">
            <div className="p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 space-y-3">
              <div className="font-mono-hy text-[10px] text-[#F2C94C]">// Session</div>
              <StatRow label="Clips" value={timeline.length} />
              <StatRow label="Total duration" value={`${totalDuration.toFixed(1)}s`} valueClass="text-[#F2C94C]" />
              <StatRow label="Export aspect" value={aspect} valueClass="text-[#00E5FF]" />
              <StatRow label="History" value={`${undoStack.length}↶ / ${redoStack.length}↷`} valueClass="text-white" />
              <button
                onClick={() => { mutate(() => []); setSelectedIdx(null); setExportResult(null); setCapturedFrame(null); }}
                disabled={timeline.length === 0}
                data-testid="editor-clear-timeline-btn"
                className="w-full mt-2 font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <X className="w-3.5 h-3.5" /> Clear timeline
              </button>
            </div>
          </aside>
        </div>

        {/* Master Scrubber — absolute timeline playhead with second + frame readout */}
        {timeline.length > 0 && (
          <div className="mt-4 p-3 rounded-2xl bg-[#120E15] border border-[#00E5FF]/25" data-testid="editor-master-scrubber">
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="font-mono-hy text-[10px] text-[#00E5FF] flex items-center gap-1.5">
                <Scissors className="w-3 h-3" /> PRECISION SCRUBBER
              </div>
              <div className="font-mono-hy text-[10px] flex items-center gap-3 tabular-nums">
                <span className="text-[#F2C94C]" data-testid="scrubber-time">{absolutePlayhead.toFixed(2)}s</span>
                <span className="text-white">/</span>
                <span className="text-white">{totalDuration.toFixed(2)}s</span>
                <span className="text-white">·</span>
                <span className="text-[#00E5FF]" data-testid="scrubber-frame">Frame {currentFrame}/{totalFrames}</span>
              </div>
            </div>
            <Slider
              value={[Math.min(absolutePlayhead, totalDuration)]}
              onValueChange={(v) => seekAbsolute(v[0])}
              min={0}
              max={Math.max(0.1, totalDuration)}
              step={FRAME_STEP}
              data-testid="scrubber-slider"
            />
            {/* Second ticks — every 1s */}
            <div className="relative mt-1 h-3 select-none" data-testid="scrubber-ticks">
              {Array.from({ length: Math.floor(totalDuration) + 1 }, (_, i) => (
                <div
                  key={i}
                  className="absolute top-0 flex flex-col items-center"
                  style={{ left: `${(i / Math.max(0.1, totalDuration)) * 100}%`, transform: "translateX(-50%)" }}
                >
                  <div className="w-px h-1.5 bg-[#F2C94C]/40" />
                  <span className="font-mono-hy text-[8px] text-white">{i}s</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline strip */}
        <div className="mt-4 p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15" data-testid="editor-timeline-strip">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-hy text-[10px] text-[#F2C94C]">// Timeline · {timeline.length} clip{timeline.length !== 1 ? "s" : ""} · zoom {pxPerSec}px/s</div>
            <div className="font-mono-hy text-[10px] text-white">Drag a clip back to the library to remove · ⌘Z / ⌘⇧Z to undo/redo</div>
          </div>
          {timeline.length === 0 ? (
            <div
              className={`h-24 rounded-xl border-2 border-dashed flex items-center justify-center text-center transition-all ${
                libraryDragOver ? "border-[#F2C94C] bg-[#F2C94C]/10" : "border-[#F2C94C]/20"
              }`}
              onDragOver={(e) => { if (e.dataTransfer?.types?.includes("application/geneo-media-id")) { e.preventDefault(); setLibraryDragOver(true); } }}
              onDragLeave={() => setLibraryDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setLibraryDragOver(false);
                const id = e.dataTransfer?.getData("application/geneo-media-id");
                if (!id) return;
                const g = gallery.find((x) => x.generation_id === id);
                if (g) addToTimeline(g);
              }}
              data-testid="timeline-empty-dropzone"
            >
              <div className="font-mono-hy text-[10px] text-white">
                Empty timeline — drop a clip from the library, or click to add.
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-0 overflow-x-auto pb-2">
              {timeline.map((c, i) => {
                const t = TRANSITIONS.find((x) => x.id === (c.transition || "cut")) || TRANSITIONS[0];
                return (
                  <div key={c.clipId} className="flex items-center flex-shrink-0">
                    {i > 0 && (
                      <div className="relative group mx-1" data-testid={`timeline-connector-${i}`}>
                        {/* Connection indicator: gold+cyan chain link with the current transition badge */}
                        <div
                          className={`h-14 w-8 rounded-md flex flex-col items-center justify-center transition-all border-2 ${
                            (c.transition || "cut") === "cut"
                              ? "border-[#F2C94C]/25 bg-[#0A0A0A]"
                              : "border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_10px_-2px_rgba(0,229,255,0.5)]"
                          }`}
                        >
                          <Link2 className={`w-3.5 h-3.5 ${(c.transition || "cut") === "cut" ? "text-[#F2C94C]/60" : "text-[#00E5FF]"}`} />
                          <span className={`font-mono-hy text-[7px] mt-0.5 ${(c.transition || "cut") === "cut" ? "text-white" : "text-[#00E5FF]"}`}>
                            {t.label.slice(0, 4).toUpperCase()}
                          </span>
                        </div>
                        {/* Hover popover — 3 transition options */}
                        <div className="hidden group-hover:flex absolute bottom-full mb-2 left-1/2 -translate-x-1/2 gap-1 p-1.5 rounded-lg bg-[#120E15] border border-[#F2C94C]/40 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.8)] z-30" data-testid={`timeline-connector-popover-${i}`}>
                          {TRANSITIONS.map((opt) => {
                            const Icon = opt.icon;
                            const active = (c.transition || "cut") === opt.id;
                            return (
                              <button
                                key={opt.id}
                                onClick={() => mutate((prev) => prev.map((x, k) => (k === i ? { ...x, transition: opt.id } : x)))}
                                data-testid={`timeline-connector-${i}-${opt.id}`}
                                title={opt.hint}
                                className={`px-2 py-1 rounded-md flex items-center gap-1 font-mono-hy text-[9px] transition-all border ${
                                  active
                                    ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF]"
                                    : "border-[#F2C94C]/15 text-white hover:border-[#F2C94C]/40 hover:text-[#F2C94C]"
                                }`}
                              >
                                <Icon className="w-3 h-3" />
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => { setSelectedIdx(i); setPlayhead(0); }}
                      draggable
                      onDragStart={(e) => {
                        setDraggingIdx(i);
                        draggingIdxRef.current = i;
                        e.dataTransfer.effectAllowed = "move";
                        try {
                          e.dataTransfer.setData("text/plain", String(i));
                          e.dataTransfer.setData("application/geneo-timeline-idx", String(i));
                        } catch (err) { console.debug("dnd timeline-idx set failed", err); }
                      }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIdx(i); }}
                      onDragLeave={() => setDragOverIdx((d) => (d === i ? null : d))}
                      onDrop={(e) => { e.preventDefault(); reorderByDrag(draggingIdxRef.current, i); setDraggingIdx(null); draggingIdxRef.current = null; setDragOverIdx(null); }}
                      onDragEnd={() => { setDraggingIdx(null); draggingIdxRef.current = null; setDragOverIdx(null); }}
                      data-testid={`timeline-clip-${i}`}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                        selectedIdx === i
                          ? "border-[#00E5FF] ring-2 ring-[#00E5FF]/30"
                          : dragOverIdx === i && draggingIdx != null && draggingIdx !== i
                            ? "border-[#F2C94C] ring-2 ring-[#F2C94C]/40 scale-105"
                            : draggingIdx === i
                              ? "border-[#F2C94C]/60 opacity-40"
                              : "border-[#F2C94C]/15 hover:border-[#F2C94C]/40"
                      }`}
                      style={{ width: `${Math.max(56, Math.round(Number(c.duration) * pxPerSec))}px`, height: "90px" }}
                    >
                      <img src={c.thumb} alt="" className="w-full h-full object-cover pointer-events-none" draggable="false" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 to-transparent pointer-events-none" />
                      <div className="absolute top-1 left-1 font-mono-hy text-[9px] px-1 py-0.5 rounded bg-[#0A0A0A]/70 text-[#F2C94C] font-bold pointer-events-none">{i + 1}</div>
                      <div className="absolute bottom-1 right-1 font-mono-hy text-[9px] text-white font-bold pointer-events-none">{Number(c.duration).toFixed(1)}s</div>
                      {c.kind === "video" && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded bg-[#EB5757]/80 flex items-center justify-center pointer-events-none">
                          <Play className="w-2.5 h-2.5 text-white fill-white" />
                        </div>
                      )}
                      {Number(c.speed || 1) !== 1 && (
                        <div className="absolute bottom-1 left-1 font-mono-hy text-[8px] px-1 py-0.5 rounded bg-[#00E5FF]/85 text-[#0A0A0A] font-bold pointer-events-none">
                          {c.speed}x
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* Frame-capture popup — appears immediately after Capture Frame with
          the two primary CTAs (Save to Gallery · Use as Reference), plus
          secondary Export PNG / Send to Pipeline. Styled in the Afro Sci-Fi
          theme: gold on primary, cyan on secondary, dark obsidian bg. */}
      <Dialog open={showCaptureDialog && !!capturedFrame} onOpenChange={setShowCaptureDialog}>
        <DialogContent
          data-testid="capture-frame-dialog"
          className="bg-gradient-to-br from-[#0F0B15] via-[#120E15] to-[#0d1f24] border-2 border-[#F2C94C]/40 shadow-[0_0_60px_-8px_rgba(242,201,76,0.35)] max-w-md rounded-2xl"
        >
          {/* Corner ornaments — echoes SceneNode's Afro Sci-Fi frame */}
          <div aria-hidden className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#F2C94C]/60 rounded-tl-2xl pointer-events-none" />
          <div aria-hidden className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#00E5FF]/60 rounded-tr-2xl pointer-events-none" />
          <div aria-hidden className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#00E5FF]/60 rounded-bl-2xl pointer-events-none" />
          <div aria-hidden className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#F2C94C]/60 rounded-br-2xl pointer-events-none" />

          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#F2C94C]" strokeWidth={2.5} />
              Still frame captured
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-white/70">
              {capturedFrame?.width ? `${capturedFrame.width}×${capturedFrame.height} · ` : ""}
              Grabbed at {Number(capturedFrame?.capturedAt || 0).toFixed(2)}s
            </DialogDescription>
          </DialogHeader>

          {/* Preview */}
          {capturedFrame?.dataUrl && (
            <div className="relative rounded-xl overflow-hidden border border-[#F2C94C]/25 bg-[#0A0A0A]">
              <img
                src={capturedFrame.dataUrl}
                alt="Captured frame preview"
                data-testid="capture-dialog-preview"
                className="w-full max-h-[280px] object-contain"
              />
              {capturedFrame.image_url && (
                <span className="absolute top-2 right-2 font-mono-hy text-[9px] tracking-widest px-2 py-1 rounded-full bg-[#27AE60]/90 text-white">
                  SAVED
                </span>
              )}
            </div>
          )}

          {/* Primary CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            <button
              type="button"
              onClick={saveFrameToGallery}
              disabled={captureBusy !== null || !!capturedFrame?.image_url}
              data-testid="capture-dialog-save-gallery"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-mono-hy text-[11px] tracking-[0.16em] border-2 border-[#00E5FF]/55 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] hover:shadow-[0_0_22px_-4px_rgba(0,229,255,0.75)] disabled:opacity-45 disabled:cursor-not-allowed transition-all"
            >
              {captureBusy === "save"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Save className="w-4 h-4" strokeWidth={2.5} />}
              {capturedFrame?.image_url ? "SAVED TO GALLERY" : "SAVE TO GALLERY"}
            </button>

            <button
              type="button"
              onClick={useCapturedAsReference}
              disabled={captureBusy !== null}
              data-testid="capture-dialog-use-reference"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-mono-hy text-[11px] tracking-[0.16em] bg-gradient-to-r from-[#F2C94C] to-[#F2D06B] text-[#0A0A0A] hover:brightness-110 disabled:opacity-45 disabled:cursor-not-allowed transition-all shadow-[0_0_22px_-4px_rgba(242,201,76,0.85)]"
            >
              {captureBusy === "reference"
                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
                : <Wand2 className="w-4 h-4" strokeWidth={2.5} />}
              USE AS REFERENCE
            </button>
          </div>

          {/* Secondary row */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={downloadFrame}
              data-testid="capture-dialog-download"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono-hy text-[10px] tracking-[0.14em] border border-[#F2C94C]/30 text-[#F2C94C]/80 hover:text-[#F2C94C] hover:bg-[#F2C94C]/10 transition-all"
            >
              <Download className="w-3 h-3" /> EXPORT PNG
            </button>
            <button
              type="button"
              onClick={sendFrameToPipeline}
              disabled={!capturedFrame?.image_url}
              data-testid="capture-dialog-to-pipeline"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono-hy text-[10px] tracking-[0.14em] border border-[#F2C94C]/30 text-[#F2C94C]/80 hover:text-[#F2C94C] hover:bg-[#F2C94C]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title={!capturedFrame?.image_url ? "Save first to enable pipeline routing" : "Send to Studio pipeline for editing"}
            >
              <ArrowRight className="w-3 h-3" /> TO PIPELINE
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Frame-series popup — 3 frames captured 1s apart. Shows a horizontal
          strip with the active frame enlarged, per-frame CTAs (SAVE ·
          ATTACH), and bulk actions (SAVE ALL · ATTACH ALL) at the bottom. */}
      <Dialog open={showCaptureDialog && !!capturedSeries} onOpenChange={setShowCaptureDialog}>
        <DialogContent
          data-testid="capture-series-dialog"
          className="bg-gradient-to-br from-[#0F0B15] via-[#120E15] to-[#0d1f24] border-2 border-[#00E5FF]/40 shadow-[0_0_60px_-8px_rgba(0,229,255,0.35)] max-w-2xl rounded-2xl"
        >
          <div aria-hidden className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#F2C94C]/60 rounded-tl-2xl pointer-events-none" />
          <div aria-hidden className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#00E5FF]/60 rounded-tr-2xl pointer-events-none" />
          <div aria-hidden className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#00E5FF]/60 rounded-bl-2xl pointer-events-none" />
          <div aria-hidden className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#F2C94C]/60 rounded-br-2xl pointer-events-none" />

          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#00E5FF]" strokeWidth={2.5} />
              3-frame series captured
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-white/70">
              {capturedSeries?.frames?.[0]?.width
                ? `${capturedSeries.frames[0].width}×${capturedSeries.frames[0].height} · `
                : ""}
              Spaced 1s apart. Save individually, or hit Save All / Attach All below.
            </DialogDescription>
          </DialogHeader>

          {/* Active preview */}
          {capturedSeries?.frames?.[capturedSeries.active] && (
            <div className="relative rounded-xl overflow-hidden border border-[#00E5FF]/25 bg-[#0A0A0A]">
              <img
                src={capturedSeries.frames[capturedSeries.active].dataUrl}
                alt={`Series frame ${capturedSeries.active + 1}`}
                data-testid="capture-series-preview"
                className="w-full max-h-[300px] object-contain"
              />
              <span className="absolute top-2 left-2 font-mono-hy text-[10px] tracking-widest px-2 py-1 rounded-full bg-[#0A0A0A]/85 text-[#00E5FF] border border-[#00E5FF]/40">
                FRAME {capturedSeries.active + 1}/3 · {Number(capturedSeries.frames[capturedSeries.active].capturedAt).toFixed(2)}s
              </span>
              {capturedSeries.frames[capturedSeries.active].image_url && (
                <span className="absolute top-2 right-2 font-mono-hy text-[9px] tracking-widest px-2 py-1 rounded-full bg-[#27AE60]/90 text-white">
                  SAVED
                </span>
              )}
            </div>
          )}

          {/* Thumb strip — 3 mini previews, active one highlighted. Click to switch. */}
          <div className="flex items-center justify-center gap-2 pt-1" data-testid="capture-series-strip">
            {(capturedSeries?.frames || []).map((f, i) => {
              const isActive = capturedSeries.active === i;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCapturedSeries((prev) => prev ? { ...prev, active: i } : prev)}
                  data-testid={`capture-series-thumb-${i}`}
                  className={`relative w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                    isActive
                      ? "border-[#F2C94C] shadow-[0_0_18px_-4px_rgba(242,201,76,0.85)] scale-[1.05]"
                      : "border-[#F2C94C]/20 hover:border-[#F2C94C]/60 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={f.dataUrl} alt={`Frame ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                  <span className="absolute bottom-0 inset-x-0 py-0.5 text-center font-mono-hy text-[9px] tracking-widest bg-[#0A0A0A]/85 text-white">
                    {i + 1} · {Number(f.capturedAt).toFixed(1)}s
                  </span>
                  {f.image_url && (
                    <span className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-[#27AE60] flex items-center justify-center">
                      <Save className="w-2 h-2 text-white" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Per-frame CTAs — act on the currently-active frame */}
          <div className="grid grid-cols-2 gap-2.5 pt-1">
            <button
              type="button"
              onClick={() => saveSeriesFrameToGallery(capturedSeries?.active ?? 0)}
              disabled={seriesBusy !== null || !!capturedSeries?.frames?.[capturedSeries?.active]?.image_url}
              data-testid="capture-series-save-active"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-mono-hy text-[10px] tracking-[0.16em] border-2 border-[#00E5FF]/55 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] hover:shadow-[0_0_22px_-4px_rgba(0,229,255,0.75)] disabled:opacity-45 disabled:cursor-not-allowed transition-all"
            >
              {seriesBusy === `save-${capturedSeries?.active}`
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Save className="w-4 h-4" strokeWidth={2.5} />}
              SAVE FRAME {(capturedSeries?.active ?? 0) + 1}
            </button>
            <button
              type="button"
              onClick={() => attachSeriesFrame(capturedSeries?.active ?? 0)}
              disabled={seriesBusy !== null}
              data-testid="capture-series-attach-active"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-mono-hy text-[10px] tracking-[0.16em] bg-gradient-to-r from-[#F2C94C] to-[#F2D06B] text-[#0A0A0A] hover:brightness-110 disabled:opacity-45 disabled:cursor-not-allowed transition-all shadow-[0_0_22px_-4px_rgba(242,201,76,0.85)]"
            >
              {seriesBusy === `attach-${capturedSeries?.active}`
                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
                : <Wand2 className="w-4 h-4" strokeWidth={2.5} />}
              ATTACH FRAME {(capturedSeries?.active ?? 0) + 1}
            </button>
          </div>

          {/* Bulk actions — apply to all 3 frames at once */}
          <div className="grid grid-cols-2 gap-2.5 pt-1 pb-1 border-t border-white/[0.06] mt-2">
            <button
              type="button"
              onClick={saveAllSeriesToGallery}
              disabled={seriesBusy !== null || (capturedSeries?.frames || []).every((f) => !!f.image_url)}
              data-testid="capture-series-save-all"
              className="inline-flex items-center justify-center gap-2 mt-2 px-4 py-2.5 rounded-full font-mono-hy text-[10px] tracking-[0.16em] border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-45 disabled:cursor-not-allowed transition-all"
            >
              {seriesBusy === "save-all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              SAVE ALL · 3 FRAMES
            </button>
            <button
              type="button"
              onClick={attachAllSeriesAsReferences}
              disabled={seriesBusy !== null || !capturedSeries?.frames?.length}
              data-testid="capture-series-attach-all"
              className="inline-flex items-center justify-center gap-2 mt-2 px-4 py-2.5 rounded-full font-mono-hy text-[10px] tracking-[0.16em] border-2 border-[#F2C94C] text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:shadow-[0_0_22px_-4px_rgba(242,201,76,0.75)] disabled:opacity-45 disabled:cursor-not-allowed transition-all"
            >
              {seriesBusy === "attach-all" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <PinIcon className="w-4 h-4" strokeWidth={2.5} />}
              ATTACH ALL TO STUDIO
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ToolbarBtn = ({ onClick, disabled, testid, icon: Icon, label, accent }) => (
  <ToolChip
    icon={Icon}
    label={label || undefined}
    onClick={onClick}
    disabled={disabled}
    accent={accent === "red" ? "red" : accent === "gold" ? "gold" : "cyan"}
    shape="square"
    size="sm"
    variant="ghost"
    testId={testid}
    title={label || testid}
  />
);

const StatRow = ({ label, value, valueClass = "text-white" }) => (
  <div className="flex items-center justify-between font-mono-hy text-[10px]">
    <span className="text-white">{label}</span>
    <span className={`${valueClass} font-bold tabular-nums`}>{value}</span>
  </div>
);
